import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Modal,
  Typography,
  Button,
  IconButton,
  Stack,
  Divider,
  Tabs,
  Tab,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChangeCredentials,
  GetProviderCredentials,
  GetNationalNominee,
} from "../../Service/UserService";
import { GetCompanyLogin } from "../../Service/CompanyLoginService";
import { ToastError } from "../../UiComponents/Toaster/Toast";
import { PROVIDER_COMPANIES } from "../../Shared/CommonConstant";
import { isNational } from "../Policy/OnlinePolicy/utils/companyRules";
import { nationalNomineeRelations } from "../Policy/OnlinePolicy/constants/formOptions";
import { useOnlinePolicyConfig } from "../Policy/OnlinePolicy/hooks/useOnlinePolicyConfig";
import { credentialSettingsShown } from "../Policy/OnlinePolicy/utils/credentialVisibility";
import { isSuperAdminLogin } from "../../Shared/policyPaymentHoldFlag";

const normalizeName = (value) => String(value || "").trim().toLowerCase();

/** The login list is projected with a leading apostrophe so Excel keeps it as
 *  text (`{ $concat: ["'", "$userIdNumber"] }` in companyLoginDao) — strip it
 *  before it ever reaches an input. */
const stripQuote = (value) => String(value ?? "").replace(/'/g, "").trim();

/** The tab is named after the insurer ("Reliance"), the saved login carries the
 *  company master's shortName ("RELIANCE GENERAL"), and neither is typed with
 *  guaranteed casing. Exact match first, then a conservative partial one. */
const isSameCompany = (loginCompany, tabCompany) => {
  const a = normalizeName(loginCompany);
  const b = normalizeName(tabCompany);
  if (!a || !b) return false;
  if (a === b) return true;
  return b.length > 2 && (a.includes(b) || b.includes(a));
};

const ChangePasswordModal = ({ open, onClose }) => {
  // The portal login settings (right-hand panel): a `user` login sees them only while SHOW_USER_CRETENTIAL_UBDATE is on;
  // every other login always does.
  // The super admin keeps no insurer portal logins (2026-10-22, the server refuses them too): password change only.
  const superAdmin = isSuperAdminLogin();
  const credentialSwitch = useOnlinePolicyConfig().showUserCredentialUpdate; // a hook: always called, before any condition
  const showUserCredentialUpdate = !superAdmin && credentialSettingsShown(credentialSwitch);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const availableCompanies = PROVIDER_COMPANIES;

  // Exactly one row per insurer, always in the same order. The company belongs
  // to the tab rather than to a dropdown, so the same insurer can never be
  // entered twice — the server rejects duplicates anyway (dao/userDao.js).
  const buildCredentialRows = (loaded) =>
    availableCompanies.map((company) => {
      const existing = (loaded || []).find(
        (row) =>
          (row?.companyName || "").trim().toLowerCase() === company.toLowerCase()
      );
      return {
        companyName: company,
        onlinePolicyUserName: existing?.onlinePolicyUserName || "",
        onlinePolicyPassword: existing?.onlinePolicyPassword || "",
        onlinePolicyLoginUrl: existing?.onlinePolicyLoginUrl || "",
      };
    });

  // A row is "in use" once any LOGIN box has a value — companyName is always
  // set now, so counting it would mark every row as filled.
  const isCredentialRowFilled = (row) =>
    !!(
      row?.onlinePolicyUserName?.trim() ||
      row?.onlinePolicyPassword?.trim() ||
      row?.onlinePolicyLoginUrl?.trim()
    );

  const [providerCredentials, setProviderCredentials] = useState(() =>
    PROVIDER_COMPANIES.map((companyName) => ({
      companyName,
      onlinePolicyUserName: "",
      onlinePolicyPassword: "",
      onlinePolicyLoginUrl: "",
    }))
  );
  const [credentialTab, setCredentialTab] = useState(0);

  // National's default nominee — independent of the login rows above, since a
  // user can save one without having a National portal login at all. National
  // only: the other insurers still collect their nominee on the policy itself.
  const [nationalNominee, setNationalNominee] = useState({
    name: "",
    relation: "",
    age: "",
  });

  // Saved portal logins from the Company Login master, used to auto-fill the
  // boxes below. Only auto-fill + enabled rows are kept — a disabled login is
  // one the portal has already rejected.
  const [savedLogins, setSavedLogins] = useState([]);
  const [loadingLogins, setLoadingLogins] = useState(false);
  // Which tab's suggestion list is open, and which row inside it the keyboard
  // is on. `null` / -1 means closed / nothing highlighted.
  const [openSuggestFor, setOpenSuggestFor] = useState(null);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  // Clicking the box browses EVERY saved login for that insurer; the list only
  // narrows once the user starts typing. Filtering on a box that was prefilled
  // from a previous save is what made the list look broken — a saved value that
  // matches no login left nothing to open.
  const [browseAllLogins, setBrowseAllLogins] = useState(false);

  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showOnlinePolicyPassword, setShowOnlinePolicyPassword] = useState({}); // Per-index visibility
  
  const toggleOPPasswordVisibility = (index) => {
    setShowOnlinePolicyPassword(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const userId = sessionStorage.getItem("UserId");

  // Every signed-in person manages their OWN portal logins here — not just
  // CLIENT accounts. A `user` has their own credential row (the automation
  // prefers it over the client's), so they need somewhere to set it.
  useEffect(() => {
    if (open && superAdmin) {
      // nothing to read: the portal login panel is not shown
    } else if (open) {
      fetchPolicyCredentials();
      fetchSavedLogins();
      fetchNationalNominee();
    } else {
      setOpenSuggestFor(null);
      setHighlightIndex(-1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userId]);

  // Read from the ProviderCredential collection, not from the user document —
  // logins are no longer stored there (see RayalBrokers-backend/Model/user.js).
  const fetchPolicyCredentials = () => {
    GetProviderCredentials(userId).then((creds) => {
      setProviderCredentials(buildCredentialRows(creds));
    });
  };

  // Read National's saved default nominee off the user/client record itself —
  // see the note on the nationalNominee state above for why it isn't a
  // ProviderCredential row. GetNationalNominee never rejects, so no .catch
  // needed here.
  const fetchNationalNominee = () => {
    GetNationalNominee(userId).then((nominee) => {
      setNationalNominee({
        name: nominee?.name || "",
        relation: nominee?.relation || "",
        age: nominee?.age ?? "",
      });
    });
  };

  // One read of the whole Company Login master — it is already scoped to the
  // signed-in person by the backend (own sub-code / branch only), so whatever
  // comes back is what this user is allowed to auto-fill with.
  const fetchSavedLogins = () => {
    setLoadingLogins(true);
    GetCompanyLogin()
      .then((res) => {
        const rows = Array.isArray(res?.data) ? res.data : [];
        setSavedLogins(
          rows
            .filter((row) => row?.isAutoFill === true && row?.isEnabled === true)
            .map((row) => ({
              userName: stripQuote(row?.userIdNumber),
              // Never stripped — an apostrophe can be part of a real password.
              password: String(row?.password ?? ""),
              url: String(row?.url ?? "").trim(),
              branch: String(row?.branch ?? "").trim(),
              owner: row?.userId?.name || "",
              companyName:
                row?.companyId?.shortName || row?.companyId?.companyName || "",
            }))
            // A login with no user id cannot be picked, and one with no company
            // cannot be placed under a tab.
            .filter((login) => login.userName && login.companyName)
        );
      })
      .finally(() => setLoadingLogins(false));
  };

  // Saved logins split per insurer tab. The unique index is
  // {userIdNumber, clientId, companyId}, but older rows predate it — dedupe on
  // user id + password so the same option is never listed twice.
  const loginsByCompany = useMemo(() => {
    const grouped = {};
    availableCompanies.forEach((company) => {
      const seen = new Set();
      grouped[company] = savedLogins.filter((login) => {
        if (!isSameCompany(login.companyName, company)) return false;
        const key = `${login.userName.toLowerCase()}|${login.password}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    });
    return grouped;
  }, [savedLogins, availableCompanies]);

  /** Options left after what is already typed — an empty box shows them all.
   *  A box holding a login that was just picked also shows them all, otherwise
   *  the list would narrow to that one row and there would be no way to switch
   *  to a different login without clearing the box first. */
  const suggestionsFor = (company, typed) => {
    const query = normalizeName(typed);
    const options = loginsByCompany[company] || [];
    if (!query) return options;
    if (options.some((login) => login.userName.toLowerCase() === query)) {
      return options;
    }
    return options.filter(
      (login) =>
        login.userName.toLowerCase().includes(query) ||
        normalizeName(login.branch).includes(query)
    );
  };

  /** Picking a saved login fills all three boxes at once. The URL is only
   *  overwritten when the saved login actually has one, so choosing a login
   *  never wipes a URL the user typed by hand. */
  const applySavedLogin = (index, login) => {
    setProviderCredentials((prev) =>
      prev.map((cred, i) =>
        i === index
          ? {
              ...cred,
              onlinePolicyUserName: login.userName,
              onlinePolicyPassword: login.password,
              onlinePolicyLoginUrl: login.url || cred.onlinePolicyLoginUrl || "",
            }
          : cred
      )
    );
    setOpenSuggestFor(null);
    setHighlightIndex(-1);
  };

  /** Arrow keys move through the list, Enter takes the highlighted option,
   *  Escape closes it. Nothing highlighted means the typed text stands as-is. */
  const handleUserNameKeyDown = (event, index, options) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      if (options.length === 0) return;
      event.preventDefault();
      setOpenSuggestFor(index);
      setHighlightIndex((prev) => {
        const start = event.key === "ArrowDown" ? prev + 1 : prev - 1;
        return (start + options.length) % options.length;
      });
      return;
    }
    if (event.key === "Enter") {
      if (openSuggestFor === index && options[highlightIndex]) {
        event.preventDefault();
        applySavedLogin(index, options[highlightIndex]);
      }
      return;
    }
    if (event.key === "Escape") {
      setOpenSuggestFor(null);
      setHighlightIndex(-1);
    }
  };

  const toggleOldPasswordVisibility = () => setShowOldPassword(!showOldPassword);
  const toggleNewPasswordVisibility = () => setShowNewPassword(!showNewPassword);
  const toggleConfirmPasswordVisibility = () => setShowConfirmPassword(!showConfirmPassword);

  const handleSavePassword = () => {
    if (!oldPassword || !newPassword || !confirmPassword) {
      ToastError("All password fields are required!");
      return;
    }
    if (newPassword !== confirmPassword) {
      ToastError("New password and confirm password do not match!");
      return;
    }

    const payload = {
      oldPassword,
      newPassword
    };

    ChangeCredentials(payload).then((res) => {
      if (res && !res.error) {
        setOldPassword("");
        setNewPassword("");
        setConfirmPassword("");
        // Optional: close modal if both are updated or just keep it open?
        // Let's keep it open so user knows they can still edit policy creds
      }
    });
  };

  const handleNationalNomineeChange = (field, value) => {
    setNationalNominee((prev) => ({ ...prev, [field]: value }));
  };

  const handleSavePolicyCredentials = () => {
    // Only rows the user actually filled in are sent. An untouched insurer is
    // not an error — it just means "no login of my own for this one", and the
    // automation falls back to the client's.
    const filled = providerCredentials.filter(isCredentialRowFilled);

    // A HALF-filled row is the real mistake: a username with no password would
    // be saved and then rejected by the portal at 2am.
    const incomplete = filled.find(
      (cred) =>
        !cred.onlinePolicyUserName?.trim() || !cred.onlinePolicyPassword?.trim()
    );
    if (incomplete) {
      ToastError(
        `${incomplete.companyName} needs both a user name and a password.`
      );
      return;
    }

    // The nominee is a sibling key in the payload, not a field on a credential
    // row — it must save even when the National login boxes above are empty,
    // so it never goes through isCredentialRowFilled / the incomplete-row
    // check above. Half-filled here is still the mistake to catch: a name with
    // no relation would save a nominee the portal rejects outright.
    const nomineeName = nationalNominee.name?.trim() || "";
    const nomineeRelation = nationalNominee.relation?.trim() || "";
    const nomineeAge = String(nationalNominee.age ?? "").trim();
    const nomineeTouched = !!(nomineeName || nomineeRelation || nomineeAge);

    if (nomineeTouched) {
      if (!nomineeName || !nomineeRelation || !nomineeAge) {
        ToastError(
          "National's nominee needs a name, relationship and age — or leave all three blank."
        );
        return;
      }
      if (!/^[a-zA-Z\s]+$/.test(nomineeName)) {
        ToastError("Nominee Name can only contain letters and spaces.");
        return;
      }
      const ageNumber = Number(nomineeAge);
      if (!Number.isInteger(ageNumber) || ageNumber <= 0) {
        ToastError("Nominee Age must be a positive whole number.");
        return;
      }
    }

    // An EMPTY list is meaningful — it clears every login this person had, and
    // the server soft-deletes them (isActive:false).
    ChangeCredentials({
      providerCredentials: filled.map((cred) => ({
        companyName: cred.companyName,
        onlinePolicyUserName: cred.onlinePolicyUserName.trim(),
        onlinePolicyPassword: cred.onlinePolicyPassword.trim(),
        onlinePolicyLoginUrl: (cred.onlinePolicyLoginUrl || "").trim(),
      })),
      // Sent as an explicit blank object when cleared, so the save can also
      // clear a previously-saved nominee.
      nationalNominee: {
        name: nomineeName,
        relation: nomineeRelation,
        age: nomineeAge,
      },
    }).then((res) => {
      if (res && !res.error) {
        fetchPolicyCredentials();
        fetchNationalNominee();
      }
    });
  };

  /** Blank one insurer's boxes — saving then soft-deletes that login. */
  const handleClearCredential = (index) => {
    setProviderCredentials((prev) =>
      prev.map((cred, i) =>
        i === index
          ? {
              ...cred,
              onlinePolicyUserName: "",
              onlinePolicyPassword: "",
              onlinePolicyLoginUrl: "",
            }
          : cred
      )
    );
  };

  const handleCredentialChange = (index, field, value) => {
    setProviderCredentials(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  return (
    <AnimatePresence>
      {open && (
        <Modal open={open} onClose={onClose}>
          <motion.div
            initial={{ opacity: 0, scale: 0.6, filter: "blur(10px)" }}
            animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
            exit={{ opacity: 0, scale: 0.6, filter: "blur(8px)" }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            style={{
              transformOrigin: "center",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100vh",
            }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.3 }}>
              <Box
                sx={{
                  width: showUserCredentialUpdate ? { xs: "94vw", sm: "90vw", md: 850 } : { xs: "94vw", sm: 440 },
                  maxWidth: "94vw",
                  maxHeight: { xs: "90vh", md: "92vh" },
                  overflowY: "auto",
                  bgcolor: "white",
                  p: { xs: 2, sm: 3 },
                  borderRadius: "10px",
                  boxShadow: 24,
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: "inter",
                      fontWeight: "bold",
                      fontSize: "18px",
                    }}>
                    Account &amp; Policy Settings
                  </Typography>
                  <Button 
                    onClick={onClose} 
                    size="small"
                    sx={{ color: "#667085", minWidth: "auto", p: 0.5, fontWeight: 600 }}
                  >
                    CLOSE
                  </Button>
                </Box>

                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={2}
                  sx={{ mt: 1 }}
                >
                  {/* Login Credentials Section */}
                  <Box sx={{ border: "1px solid #EAECF0", borderRadius: "8px", p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#667085", mb: 1.5 }}>
                      Login Credentials
                    </Typography>
                    
                    <Box display="flex" flexDirection="column" gap={1.5} sx={{ flex: 1 }}>
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        <Typography sx={{ fontFamily: "inter", fontSize: "13px" }}>Old Password</Typography>
                        <Box sx={{ position: "relative" }}>
                          <input
                            placeholder="Enter your old password"
                            type={showOldPassword ? "text" : "password"}
                            className="InputFiled"
                            value={oldPassword}
                            onChange={(e) => setOldPassword(e.target.value)}
                          />
                          <IconButton
                            onClick={toggleOldPasswordVisibility}
                            size="small"
                            sx={{ position: "absolute", right: 8, top: "5px" }}
                          >
                            {showOldPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </Box>
                      </Box>

                      <Box display="flex" flexDirection="column" gap={0.5}>
                        <Typography sx={{ fontFamily: "inter", fontSize: "13px" }}>New Password</Typography>
                        <Box sx={{ position: "relative" }}>
                          <input
                            placeholder="Enter a new password"
                            type={showNewPassword ? "text" : "password"}
                            className="InputFiled"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                          />
                          <IconButton
                            onClick={toggleNewPasswordVisibility}
                            size="small"
                            sx={{ position: "absolute", right: 8, top: "5px" }}
                          >
                            {showNewPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </Box>
                      </Box>

                      <Box display="flex" flexDirection="column" gap={0.5}>
                        <Typography sx={{ fontFamily: "inter", fontSize: "13px" }}>Confirm Password</Typography>
                        <Box sx={{ position: "relative" }}>
                          <input
                            placeholder="Re-enter new password"
                            type={showConfirmPassword ? "text" : "password"}
                            className="InputFiled"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                          />
                          <IconButton
                            onClick={toggleConfirmPasswordVisibility}
                            size="small"
                            sx={{ position: "absolute", right: 8, top: "5px" }}
                          >
                            {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </Box>
                      </Box>

                      <Button
                        fullWidth
                        variant="contained"
                        sx={{
                          backgroundColor: "#1E3A5F",
                          textTransform: "none",
                          fontWeight: 600,
                          mt: 'auto',
                          pt: 1,
                          "&:hover": { backgroundColor: "#142a47" },
                        }}
                        onClick={handleSavePassword}
                      >
                        Save Password
                      </Button>
                    </Box>
                  </Box>

                  {/* Online Policy Credentials Section */}
                  {/* Online Policy Credentials — shown to EVERY signed-in person while SHOW_USER_CRETENTIAL_UBDATE is on.
                      One tab per insurer, so a company can never be entered twice. */}
                  {showUserCredentialUpdate && (
                  <Box
                    sx={{
                      border: "1px solid #EAECF0",
                      borderRadius: "8px",
                      flex: 1.5,
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                    }}
                  >
                    {/* Navy bar, white labels, gold on hover and when active — the same
                        trio as .NavHeading / the dropdown h1:hover in Navbar.scss. */}
                    <Tabs
                      value={credentialTab}
                      onChange={(e, next) => {
                        setCredentialTab(next);
                        setOpenSuggestFor(null);
                        setHighlightIndex(-1);
                      }}
                      variant="fullWidth"
                      sx={{
                        minHeight: "44px",
                        backgroundColor: "#1E3A5F",
                        "& .MuiTab-root": {
                          minHeight: "44px",
                          textTransform: "none",
                          fontFamily: "'Poppins', sans-serif",
                          fontSize: "13px",
                          fontWeight: 500,
                          color: "#FFFFFF",
                          opacity: 1,
                          transition: "color .2s ease, background-color .2s ease",
                          "&:hover": { color: "#D4AF37", backgroundColor: "#2E5A8F" },
                        },
                        "& .Mui-selected": { color: "#D4AF37 !important", fontWeight: 600 },
                        "& .MuiTabs-indicator": { height: "3px", backgroundColor: "#D4AF37" },
                      }}
                    >
                      {availableCompanies.map((company, index) => (
                        <Tab
                          key={company}
                          disableRipple
                          label={
                            <Stack direction="row" alignItems="center" spacing={0.75}>
                              <span>{company}</span>
                              {/* Gold dot = a login is saved for this insurer. */}
                              <Box
                                component="span"
                                sx={{
                                  width: 7,
                                  height: 7,
                                  borderRadius: "50%",
                                  backgroundColor: isCredentialRowFilled(providerCredentials[index])
                                    ? "#D4AF37"
                                    : "rgba(255,255,255,0.35)",
                                }}
                              />
                            </Stack>
                          }
                        />
                      ))}
                    </Tabs>

                    <Box sx={{ p: 2, flex: 1 }}>
                      {availableCompanies.map((company, index) => {
                        const cred = providerCredentials[index] || {};
                        const filled = isCredentialRowFilled(cred);
                        const savedForCompany = loginsByCompany[company] || [];
                        const matching = suggestionsFor(
                          company,
                          cred.onlinePolicyUserName
                        );
                        // Browsing, or a search that matched nothing, falls back to
                        // the full list so the box never opens empty.
                        const options =
                          browseAllLogins || matching.length === 0
                            ? savedForCompany
                            : matching;
                        const showSuggestions =
                          openSuggestFor === index && options.length > 0;

                        return (
                          <Box
                            key={company}
                            // Kept mounted and hidden — remounting would clear the
                            // per-tab password-visibility toggles on every switch.
                            sx={{ display: credentialTab === index ? "block" : "none" }}
                          >
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
                              <Box>
                                <Typography
                                  sx={{
                                    fontFamily: "'Poppins', sans-serif",
                                    fontWeight: 600,
                                    fontSize: "13px",
                                    color: "#1E3A5F",
                                  }}
                                >
                                  {company} Portal Login
                                </Typography>
                                <Box sx={{ width: 30, height: 2, borderRadius: 1, backgroundColor: "#D4AF37", mt: 0.6, mb: 0.6 }} />
                                <Typography sx={{ fontSize: "11.5px", color: "#667085" }}>
                                  {filled
                                    ? "Your own login — used ahead of your client's."
                                    : "Leave blank to use your client's login."}
                                </Typography>
                              </Box>

                              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
                                <Tooltip title="Reload the saved logins">
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={fetchSavedLogins}
                                      disabled={loadingLogins}
                                    >
                                      {loadingLogins ? (
                                        <CircularProgress size={16} />
                                      ) : (
                                        <RefreshIcon fontSize="small" sx={{ color: "#1E3A5F" }} />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>

                                {filled && (
                                  <Button
                                    size="small"
                                    color="error"
                                    startIcon={<DeleteIcon />}
                                    onClick={() => handleClearCredential(index)}
                                    sx={{ textTransform: "none", fontSize: "12px", flexShrink: 0 }}
                                  >
                                    Clear
                                  </Button>
                                )}
                              </Stack>
                            </Stack>

                            <Box display="flex" flexDirection="column" gap={1.5}>
                              {/* Free-typing box with a search-as-you-type list of the
                                  insurer's saved auto-fill logins. Picking one fills the
                                  password and URL too; anything typed that matches no
                                  saved login is kept exactly as entered. */}
                              <Box display="flex" flexDirection="column" gap={0.5}>
                                <Typography sx={{ fontFamily: "inter", fontSize: "12px" }}>User Name</Typography>
                                <Box sx={{ position: "relative" }}>
                                  <input
                                    className="InputFiled"
                                    autoComplete="off"
                                    placeholder={
                                      savedForCompany.length > 0
                                        ? "Type to search a saved login, or enter your own"
                                        : "Portal user name"
                                    }
                                    value={cred.onlinePolicyUserName || ""}
                                    onChange={(e) => {
                                      handleCredentialChange(index, "onlinePolicyUserName", e.target.value);
                                      setOpenSuggestFor(index);
                                      setBrowseAllLogins(false);
                                      setHighlightIndex(-1);
                                    }}
                                    onFocus={() => {
                                      setOpenSuggestFor(index);
                                      setBrowseAllLogins(true);
                                      setHighlightIndex(-1);
                                    }}
                                    // A click on an ALREADY focused box fires no focus
                                    // event — without this the list stays shut after a
                                    // pick until the box is cleared.
                                    onClick={() => {
                                      setOpenSuggestFor(index);
                                      setBrowseAllLogins(true);
                                      setHighlightIndex(-1);
                                    }}
                                    // The list closes on blur; the options keep focus on the
                                    // input (onMouseDown below) so a click still registers.
                                    onBlur={() => {
                                      setOpenSuggestFor(null);
                                      setHighlightIndex(-1);
                                    }}
                                    onKeyDown={(e) => handleUserNameKeyDown(e, index, options)}
                                    style={{ paddingRight: 34 }}
                                  />

                                  {savedForCompany.length > 0 && (
                                    <Box
                                      component="span"
                                      sx={{
                                        position: "absolute",
                                        right: 10,
                                        top: 12,
                                        fontSize: "10px",
                                        color: "#667085",
                                        pointerEvents: "none",
                                      }}
                                    >
                                      ▼
                                    </Box>
                                  )}

                                  {showSuggestions && (
                                    <Box
                                      onMouseDown={(e) => e.preventDefault()}
                                      sx={{
                                        position: "absolute",
                                        top: "44px",
                                        left: 0,
                                        right: 0,
                                        zIndex: 20,
                                        maxHeight: 190,
                                        overflowY: "auto",
                                        backgroundColor: "#fff",
                                        border: "1px solid #EAECF0",
                                        borderRadius: "6px",
                                        boxShadow: "0 6px 16px rgba(16,24,40,0.12)",
                                      }}
                                    >
                                      {options.map((login, optionIndex) => (
                                        <Box
                                          key={`${login.userName}-${login.password}-${optionIndex}`}
                                          onClick={() => applySavedLogin(index, login)}
                                          onMouseEnter={() => setHighlightIndex(optionIndex)}
                                          sx={{
                                            px: 1.2,
                                            py: 0.8,
                                            cursor: "pointer",
                                            backgroundColor:
                                              highlightIndex === optionIndex ? "#F2F6FC" : "transparent",
                                            borderBottom: "1px solid #F5F6F8",
                                            "&:last-of-type": { borderBottom: "none" },
                                          }}
                                        >
                                          <Typography
                                            sx={{ fontSize: "12.5px", fontWeight: 600, color: "#1E3A5F" }}
                                          >
                                            {login.userName}
                                          </Typography>
                                          <Typography
                                            sx={{ fontSize: "11px", color: "#667085", wordBreak: "break-all" }}
                                          >
                                            {[login.branch, login.owner, login.url]
                                              .filter(Boolean)
                                              .join(" • ") || "Saved login"}
                                          </Typography>
                                        </Box>
                                      ))}
                                    </Box>
                                  )}
                                </Box>

                                <Typography sx={{ fontSize: "11px", color: "#98A2B3" }}>
                                  {loadingLogins
                                    ? "Loading saved logins…"
                                    : savedForCompany.length > 0
                                    ? `${savedForCompany.length} saved login${
                                        savedForCompany.length > 1 ? "s" : ""
                                      } for ${company} — pick one to fill the password and URL.`
                                    : `No saved auto-fill login for ${company} — enter it manually.`}
                                </Typography>
                              </Box>

                              <Box display="flex" flexDirection="column" gap={0.5}>
                                <Typography sx={{ fontFamily: "inter", fontSize: "12px" }}>Password</Typography>
                                <Box sx={{ position: "relative" }}>
                                    <input
                                      className="InputFiled"
                                      placeholder="Portal password"
                                      type={showOnlinePolicyPassword[index] ? "text" : "password"}
                                      value={cred.onlinePolicyPassword || ""}
                                      onChange={(e) => handleCredentialChange(index, "onlinePolicyPassword", e.target.value)}
                                      style={{ paddingRight: 40 }}
                                    />
                                    <IconButton
                                      onClick={() => toggleOPPasswordVisibility(index)}
                                      size="small"
                                      sx={{ position: "absolute", right: 8, top: "5px" }}
                                    >
                                      {showOnlinePolicyPassword[index] ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                    </IconButton>
                                </Box>
                              </Box>

                              <Box display="flex" flexDirection="column" gap={0.5}>
                                <Typography sx={{ fontFamily: "inter", fontSize: "12px" }}>
                                  Login URL <span style={{ color: "#98A2B3" }}>(optional)</span>
                                </Typography>
                                <input
                                  className="InputFiled"
                                  placeholder={"https://... " + company + " portal"}
                                  value={cred.onlinePolicyLoginUrl || ""}
                                  onChange={(e) => handleCredentialChange(index, "onlinePolicyLoginUrl", e.target.value)}
                                />
                              </Box>

                              {/* National only — its portal now demands a nominee on
                                  EVERY quote, including policies with no PA cover, where
                                  the Online Policy form never asks for one. Saved here
                                  as an independent default: it has no bearing on the
                                  login boxes above and saves even when they're empty. */}
                              {isNational(company) && (
                                <>
                                  <Box>
                                    <Typography
                                      sx={{
                                        fontFamily: "'Poppins', sans-serif",
                                        fontWeight: 600,
                                        fontSize: "13px",
                                        color: "#1E3A5F",
                                      }}
                                    >
                                      Nominee Details
                                    </Typography>
                                    <Box sx={{ width: 30, height: 2, borderRadius: 1, backgroundColor: "#D4AF37", mt: 0.6, mb: 0.6 }} />
                                    <Typography sx={{ fontSize: "11.5px", color: "#667085" }}>
                                      Used when a policy has no nominee of its own — National asks for one on every quote.
                                    </Typography>
                                  </Box>

                                  <Box display="flex" flexDirection="column" gap={0.5}>
                                    <Typography sx={{ fontFamily: "inter", fontSize: "12px" }}>Nominee Name</Typography>
                                    <input
                                      className="InputFiled"
                                      placeholder="Nominee's full name"
                                      value={nationalNominee.name || ""}
                                      onChange={(e) => handleNationalNomineeChange("name", e.target.value)}
                                    />
                                  </Box>

                                  <Box display="flex" flexDirection="column" gap={0.5}>
                                    <Typography sx={{ fontFamily: "inter", fontSize: "12px" }}>Relationship with Nominee</Typography>
                                    <select
                                      className="InputFiled"
                                      value={nationalNominee.relation || ""}
                                      onChange={(e) => handleNationalNomineeChange("relation", e.target.value)}
                                    >
                                      <option value="">Select relationship</option>
                                      {nationalNomineeRelations.map((option) => (
                                        <option key={option.value} value={option.value}>
                                          {option.label}
                                        </option>
                                      ))}
                                    </select>
                                  </Box>

                                  <Box display="flex" flexDirection="column" gap={0.5}>
                                    <Typography sx={{ fontFamily: "inter", fontSize: "12px" }}>Nominee Age</Typography>
                                    <input
                                      className="InputFiled"
                                      type="number"
                                      min="1"
                                      placeholder="Nominee's age"
                                      value={nationalNominee.age ?? ""}
                                      onChange={(e) => handleNationalNomineeChange("age", e.target.value)}
                                    />
                                  </Box>
                                </>
                              )}
                            </Box>
                          </Box>
                        );
                      })}

                      {/* Saves ALL three tabs at once, so switching tabs never loses an edit. */}
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={handleSavePolicyCredentials}
                        sx={{
                          mt: 2.5,
                          backgroundColor: "#1E3A5F",
                          textTransform: "none",
                          fontWeight: 600,
                          "&:hover": { backgroundColor: "#2E5A8F" },
                        }}
                      >
                        Save Credentials
                      </Button>
                    </Box>
                  </Box>
                  )}
                </Stack>
              </Box>
            </motion.div>
          </motion.div>
        </Modal>
      )}
    </AnimatePresence>
  );
};

export default ChangePasswordModal;
